/**
 * @file: ProductReferralRewardDepositedBody.tsx
 *
 * `OrgProductReferralRewardDeposited` — a product-referral commission
 * landed in the member's account. Prominent green +amount + the
 * resulting balance, then a per-tx breakdown rendered the same way
 * the account-detail page (`AccountTransactionsScreen`) renders each
 * tx row: tx-type label colored by kind, plus the product-referral
 * context (lpn / vehicle / subject / product name + qty) parsed out
 * of the snapshot's raw `extra` JSON so the user can tell which
 * vehicle the deposit traces back to without leaving the feed.
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  OrgMemberAccountTxDetail,
  OrgMemberAccountTxProductReferralInfo,
} from '@euler/model/OrgMemberAccountTxDetail';
import {
  OrgMemberAccountTxSnapshot,
  OrgProductReferralRewardDepositedFeedPayload,
} from '@euler/model/feed';
import { FC } from 'react';
import { View } from 'react-native';
import { formatYuan, txTypeInfo } from './common/labels';
import { valueColor } from './common/primitives';

export const ProductReferralRewardDepositedBody: FC<{
  p: OrgProductReferralRewardDepositedFeedPayload;
}> = ({ p }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View>
      <View
        css={`
          flex-direction: row;
          align-items: baseline;
        `}
      >
        <Typography.Text size={rpx(22)} color="#16a34a" bold>
          +¥{formatYuan(p.amount)}
        </Typography.Text>
        <Typography.Text ml={rpx(8)} size={rpx(12)} color="#6b7280">
          推广分润已入账
        </Typography.Text>
      </View>
      <Typography.Text mt={rpx(4)} size={rpx(12)} color="#6b7280">
        账户余额 ¥{formatYuan(p.balance)}
      </Typography.Text>
      {p.transactions && p.transactions.length > 0 ? (
        <View
          css={`
            margin-top: ${rpx(8)}px;
          `}
        >
          {p.transactions.map((tx, idx) => (
            <View
              key={String(tx.id)}
              css={`
                ${idx > 0
                  ? `border-top-width: 1px; border-top-color: #e5e7eb;`
                  : ''}
              `}
            >
              <TxBreakdownRow tx={tx} />
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
};

/**
 * Try to parse the snapshot's raw `extra` JSON into the structured
 * detail. Returns `undefined` on missing / malformed extras so the
 * row degrades to title + amount only.
 */
function parseTxDetail(
  extra: string | undefined,
): OrgMemberAccountTxDetail | undefined {
  if (!extra) return undefined;
  try {
    return JSON.parse(extra) as OrgMemberAccountTxDetail;
  } catch {
    return undefined;
  }
}

function productReferralOf(
  detail: OrgMemberAccountTxDetail | undefined,
): OrgMemberAccountTxProductReferralInfo | undefined {
  if (detail == null) return undefined;
  if (
    detail.type === 'productReferral' ||
    detail.type === 'productReferralRevoke'
  ) {
    // The revoke variant types `productReferral` as a `ProductReferral`
    // entity (not the display-info shape), but the keys we display
    // (licensePlateNo / vehicleName / subject / productName) line up
    // 1:1 with the entity, so cross-narrow via the common shape.
    return detail.productReferral as OrgMemberAccountTxProductReferralInfo;
  }
  return undefined;
}

const TxBreakdownRow: FC<{ tx: OrgMemberAccountTxSnapshot }> = ({ tx }) => {
  const { rpx } = useResponsiveContext();
  const ti = txTypeInfo(tx.txType);
  const productReferral = productReferralOf(parseTxDetail(tx.extra));
  return (
    <View
      css={`
        padding: ${rpx(8)}px 0;
      `}
    >
      <View
        css={`
          flex-direction: row;
          justify-content: space-between;
          align-items: baseline;
        `}
      >
        <Typography.Text size={rpx(13)} color={ti.color} bold>
          {ti.text}
        </Typography.Text>
        <Typography.Text size={rpx(13)} color={ti.color} medium>
          +¥{formatYuan(tx.amount)}
        </Typography.Text>
      </View>
      {productReferral ? (
        <View
          css={`
            margin-top: ${rpx(4)}px;
          `}
        >
          <DetailLine label="车牌号码" value={productReferral.licensePlateNo} />
          <DetailLine label="车型名称" value={productReferral.vehicleName} />
          <DetailLine label="施工项目" value={productReferral.subject} />
          <DetailLine
            label="产品名称"
            value={productReferral.productName}
            qty={productReferral.qty}
          />
        </View>
      ) : null}
      {tx.remark ? (
        <Typography.Text
          mt={rpx(4)}
          size={rpx(11)}
          color="#6b7280"
          numberOfLines={2}
        >
          {tx.remark}
        </Typography.Text>
      ) : null}
    </View>
  );
};

const DetailLine: FC<{
  label: string;
  value: string | null | undefined;
  qty?: number;
}> = ({ label, value, qty }) => {
  const { rpx } = useResponsiveContext();
  if (!value) return null;
  return (
    <View
      css={`
        flex-direction: row;
        margin-top: ${rpx(2)}px;
      `}
    >
      <Typography.Text size={rpx(12)} color="#6b7280">
        {label}: {value}
      </Typography.Text>
      {qty != null && qty !== 1 ? (
        <Typography.Text
          size={rpx(12)}
          color={valueColor}
          style={{ fontStyle: 'italic', marginLeft: rpx(4) }}
        >
          x{qty}
        </Typography.Text>
      ) : qty === 1 ? (
        <Typography.Text
          size={rpx(12)}
          color="#9ca3af"
          style={{ marginLeft: rpx(4) }}
        >
          x1
        </Typography.Text>
      ) : null}
    </View>
  );
};
